import { useState, useEffect, useMemo } from 'react'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useQueryParamState } from '@/hooks/useQueryParamState'
import { Plus, Trash2, Pencil, RotateCcw, BookOpen, Info } from 'lucide-react'
import {
  Button,
  EmptyState,
  Input,
  Label,
  Badge,
  Select,
  Tooltip,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui'
import { SearchInput } from '@/components/SearchInput'
import { ResizablePanels } from '@/components/ResizablePanels'
import { hintsAPI, type Hint } from '@/lib/api'
import { useAppContext } from '@/hooks/useAppContext'
import { useToast } from '@/hooks/useToast'
import { useConfirm } from '@/hooks/useConfirm'
import { useAsyncAction } from '@/hooks/useAsyncAction'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { formatLocalDateTime } from '@/lib/utils'
import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog'

const CATEGORY_OPTIONS = [
  { value: 'selector', label: 'Selector' },
  { value: 'assertion', label: 'Assertion' },
  { value: 'timing', label: 'Timing' },
  { value: 'interaction', label: 'Interaction' },
  { value: 'navigation', label: 'Navigation' },
  { value: 'structure', label: 'Structure' },
]

const SOURCE_OPTIONS = [
  { value: 'generator', label: 'Generator' },
  { value: 'analyzer', label: 'Analyzer' },
  { value: 'healer', label: 'Healer' },
  { value: 'custom', label: 'Custom' },
]

const CATEGORY_BADGE: Record<string, string> = {
  selector: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  assertion: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
  timing: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  interaction: 'bg-green-500/10 text-green-500 border-green-500/30',
  navigation: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/30',
  structure: 'bg-rose-500/10 text-rose-500 border-rose-500/30',
}

/** List health dot — driven by the server-computed efficacy bucket,
 * so it agrees with the Track-record panel and the Analytics report.
 * Grey = unrated (too few adoptions to judge yet). */
function healthDot(hint: Hint): string {
  if (hint.bucket === 'effective') return 'bg-green-500'
  if (hint.bucket === 'deadWeight') return 'bg-red-500'
  return 'bg-muted-foreground'
}

/** One box in the injected → adopted → effective / failed funnel,
 * with a hover explanation. */
function EfficacyStat({
  value,
  label,
  tip,
  valueClass,
}: {
  value: number
  label: string
  tip: string
  valueClass?: string
}) {
  return (
    <div className="text-center p-2.5 rounded-lg bg-muted">
      <div className={`text-xl font-bold ${valueClass ?? ''}`}>{value}</div>
      <div className="flex items-center justify-center gap-1 text-[10px] text-muted-foreground">
        {label}
        <Tooltip content={tip} triggerLabel={`About ${label}`}>
          <Info aria-hidden="true" className="h-2.5 w-2.5" />
        </Tooltip>
      </div>
    </div>
  )
}

export default function HintsPage() {
  const { selectedVariantId } = useAppContext()
  const toast = useToast()
  const tz = useUserTimezone()
  // #1215: migrated from a hand-rolled <Dialog>-based confirm to the
  // shared `useConfirm` primitive so deactivate / delete copy + theming
  // match the rest of the app.
  const confirm = useConfirm()
  const [hints, setHints] = useState<Hint[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Filters
  const [filterCategory, setFilterCategory] = usePersistedState('hints-filter-category', 'all')
  const [filterSource, setFilterSource] = usePersistedState('hints-filter-source', 'all')
  const [filterStatus, setFilterStatus] = usePersistedState<'all' | 'active' | 'injected' | 'inactive'>('hints-filter-status', 'active')
  // Search lives in the URL so navigate-away-and-return restores the
  // operator's active query (#1217). Strips on empty.
  const [searchText, setSearchText] = useQueryParamState('q', '')
  const [sortBy, setSortBy] = usePersistedState<string>('hints-sort-by', 'confirmationCount')

  // Dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingHint, setEditingHint] = useState<Hint | null>(null)
  const [formCategory, setFormCategory] = useState('selector')
  const [formElement, setFormElement] = useState('')
  const [formSelector, setFormSelector] = useState('')
  const [formPattern, setFormPattern] = useState('')
  const [formAntipattern, setFormAntipattern] = useState('')

  // Type-DELETE confirmation for the bulk "Delete Agent Memory" wipe.
  // (Per-row deactivate / delete-permanent live on `confirm` from
  // useConfirm; the typed-confirm modal is reserved for the bulk wipe.)
  const [confirmingDeleteAll, setConfirmingDeleteAll] = useState(false)

  useEffect(() => {
    if (!selectedVariantId) { setHints([]); setLoading(false); return }
    setLoading(true)
    hintsAPI.getByVariant(selectedVariantId, true)
      .then(setHints)
      .catch(() => setHints([]))
      .finally(() => setLoading(false))
  }, [selectedVariantId])

  const reload = () => {
    if (!selectedVariantId) return
    hintsAPI.getByVariant(selectedVariantId, true).then(data => {
      setHints(data)
      // If selected hint was deleted, clear selection
      if (selectedId && !data.find(l => l.id === selectedId)) setSelectedId(null)
    }).catch(() => {})
  }

  // ── Handlers ──

  function openAddDialog() {
    setEditingHint(null)
    setFormCategory('selector')
    setFormElement(''); setFormSelector(''); setFormPattern(''); setFormAntipattern('')
    setIsDialogOpen(true)
  }

  function openEditDialog(hint: Hint) {
    setEditingHint(hint)
    setFormCategory(hint.category)
    setFormElement(hint.element || '')
    setFormSelector(hint.selector || '')
    setFormPattern(hint.pattern || '')
    setFormAntipattern(hint.antipattern || '')
    setIsDialogOpen(true)
  }

  // #1215: useAsyncAction so the dialog's Save button auto-disables
  // while the create/update POST is in-flight. Previously the button
  // was only gated on `!formPattern && !formSelector` — operators
  // could double-click and trigger two POSTs for the same edit.
  const saveAction = useAsyncAction({
    run: async () => {
      // Throwing — not returning — when the variant isn't selected so
      // `onSuccess` (which closes the dialog + triggers a reload) does
      // NOT fire on the no-op path. The page should be unreachable
      // without a variant, but cross-tab context drift makes this a
      // belt-and-suspenders guard. Copilot review on #1222.
      if (!selectedVariantId) throw new Error('Select an application + variant before saving.')
      if (editingHint) {
        await hintsAPI.update(editingHint.id, {
          element: formElement || undefined,
          selector: formSelector || undefined,
          pattern: formPattern || undefined,
          antipattern: formAntipattern || undefined,
        })
      } else {
        await hintsAPI.create({
          variantId: selectedVariantId,
          category: formCategory,
          element: formElement || undefined,
          selector: formSelector || undefined,
          pattern: formPattern || undefined,
          antipattern: formAntipattern || undefined,
        })
      }
    },
    onSuccess: () => {
      setIsDialogOpen(false)
      reload()
    },
    onError: () => toast({ title: 'Failed to save entry', variant: 'error' }),
  })

  async function deactivateHint(id: string) {
    const ok = await confirm({
      title: 'Deactivate this memory entry?',
      description:
        'The entry stays in your library but stops being injected into agent prompts. You can reactivate it later from the Inactive filter.',
      confirmLabel: 'Deactivate',
      destructive: false,
    })
    if (!ok) return
    try { await hintsAPI.deactivate(id); reload() }
    catch { toast({ title: 'Failed to deactivate entry', variant: 'error' }) }
  }

  async function deleteHint(id: string) {
    const ok = await confirm({
      title: 'Delete this memory entry?',
      description: 'The entry is permanently removed. This cannot be undone.',
      confirmLabel: 'Delete entry',
      destructive: true,
    })
    if (!ok) return
    try { await hintsAPI.deletePermanently(id); reload() }
    catch { toast({ title: 'Failed to delete entry', variant: 'error' }) }
  }

  async function reactivateHint(id: string) {
    try { await hintsAPI.reactivate(id); reload() }
    catch { toast({ title: 'Failed to reactivate entry', variant: 'error' }) }
  }

  async function deleteAllMemories() {
    if (!selectedVariantId) return
    try {
      await hintsAPI.deleteAllByVariant(selectedVariantId)
      setSelectedId(null)
      setConfirmingDeleteAll(false)
      reload()
    } catch {
      toast({ title: 'Failed to delete agent memory', variant: 'error' })
    }
  }

  // ── Derived data ──

  const activeCount = hints.filter(l => l.active).length
  const inactiveCount = hints.length - activeCount

  const INJECTION_LIMIT = 10
  const injectedIds = useMemo(() => {
    const top = [...hints]
      .filter(l => l.active)
      .sort((a, b) => b.confirmationCount - a.confirmationCount)
      .slice(0, INJECTION_LIMIT)
    return new Set(top.map(l => l.id))
  }, [hints])

  const filtered = useMemo(() => {
    let list = hints
    if (filterCategory !== 'all') list = list.filter(l => l.category === filterCategory)
    if (filterSource !== 'all') list = list.filter(l => l.source === filterSource)
    if (filterStatus === 'active') list = list.filter(l => l.active)
    else if (filterStatus === 'injected') list = list.filter(l => injectedIds.has(l.id))
    else if (filterStatus === 'inactive') list = list.filter(l => !l.active)
    if (searchText) {
      const q = searchText.toLowerCase()
      list = list.filter(l =>
        (l.pattern || '').toLowerCase().includes(q) ||
        (l.selector || '').toLowerCase().includes(q) ||
        (l.element || '').toLowerCase().includes(q)
      )
    }
    list = [...list].sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      if (sortBy === 'effectiveness') return (b.efficacyRate || 0) - (a.efficacyRate || 0)
      return (b.confirmationCount || 0) - (a.confirmationCount || 0)
    })
    return list
  }, [hints, filterCategory, filterSource, filterStatus, searchText, sortBy])

  const selected = filtered.find(l => l.id === selectedId) || null

  // ── Render ──

  if (!selectedVariantId) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Agent Memory</h1>
        <EmptyState
          icon={BookOpen}
          title="Pick an app and variant"
          body="Memory entries are scoped to a specific variant. Choose one from the header above to view what the agents have learned."
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-[calc(100dvh-3.5rem)]">
      <div className="px-6 pt-6 pb-3 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Agent Memory</h1>
          <p className="text-sm text-muted-foreground">
            {hints.length} {hints.length === 1 ? 'entry' : 'entries'} ({activeCount} active, {inactiveCount} inactive)
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={openAddDialog}>
            <Plus className="h-3 w-3 mr-1" /> Add entry
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive"
            onClick={() => setConfirmingDeleteAll(true)}
            disabled={hints.length === 0}
          >
            <Trash2 className="h-3 w-3 mr-1" /> Delete Agent Memory
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6">
        <div className="h-full border rounded-lg overflow-hidden">
          <ResizablePanels defaultWidth={360} minWidth={280} maxWidth={500} left={
            <div className="h-full flex flex-col">
              {/* Filters */}
              <div className="p-2 border-b space-y-2">
                <SearchInput value={searchText} onChange={setSearchText} placeholder="Search entries..." />
                <div className="flex gap-1.5">
                  <Select
                    options={[{ value: 'all', label: 'All Categories' }, ...CATEGORY_OPTIONS]}
                    value={filterCategory}
                    onChange={e => setFilterCategory(e.target.value)}
                    className="flex-1 text-xs h-7"
                  />
                  <Select
                    options={[{ value: 'all', label: 'All Sources' }, ...SOURCE_OPTIONS]}
                    value={filterSource}
                    onChange={e => setFilterSource(e.target.value)}
                    className="flex-1 text-xs h-7"
                  />
                  <Select
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'injected', label: 'Injected' },
                      { value: 'inactive', label: 'Inactive' },
                      { value: 'all', label: 'All' },
                    ]}
                    value={filterStatus}
                    onChange={e => setFilterStatus(e.target.value as any)}
                    className="flex-1 text-xs h-7"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    Showing {filtered.length} of {hints.length}
                  </p>
                  <Select
                    options={[
                      { value: 'confirmationCount', label: 'Confirmed' },
                      { value: 'effectiveness', label: 'Effective' },
                      { value: 'newest', label: 'Newest' },
                    ]}
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                    className="w-28 text-xs h-7"
                  />
                </div>
              </div>

              {/* Memory-entry list */}
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <p className="text-sm text-muted-foreground p-4">Loading...</p>
                ) : filtered.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-4">
                    {hints.length === 0 ? 'No entries yet.' : 'No entries match filters.'}
                  </p>
                ) : (
                  filtered.map(hint => (
                    <button
                      key={hint.id}
                      onClick={() => setSelectedId(hint.id)}
                      className={`w-full p-3 text-left border-b border-border/50 transition-colors ${
                        selectedId === hint.id ? 'bg-primary/10 border-l-2 border-l-primary' : 'hover:bg-muted/50'
                      } ${!hint.active ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${healthDot(hint)}`} />
                        <Badge variant="outline" className={`text-[10px] shrink-0 ${CATEGORY_BADGE[hint.category] || ''}`}>
                          {hint.category}
                        </Badge>
                        {injectedIds.has(hint.id) && (
                          <Badge variant="outline" className="text-[10px] text-blue-400 border-blue-500/30 shrink-0">inj</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2 leading-relaxed">
                        {hint.pattern || hint.selector || 'No pattern'}
                      </p>
                    </button>
                  ))
                )}
              </div>
            </div>
          } right={
            !selected ? (
              hints.length === 0 && !loading ? (
                <div className="flex items-center justify-center h-full p-6">
                  <div className="max-w-md w-full">
                    <EmptyState
                      icon={BookOpen}
                      title="No memory entries yet"
                      body="The agents will populate this as they run. You can also seed an initial pattern manually — categorize by selector, assertion, timing, or navigation."
                      action={{ label: 'Add entry', onClick: openAddDialog }}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                  Select an entry to view details
                </div>
              )
            ) : (
              <div className="overflow-y-auto h-full p-4 space-y-5">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`text-xs ${CATEGORY_BADGE[selected.category] || ''}`}>
                      {selected.category}
                    </Badge>
                    <Badge variant="outline" className="text-xs">{selected.source}</Badge>
                    <Badge variant="outline" className={`text-xs ${selected.active ? 'text-green-500 border-green-500/30' : 'text-muted-foreground'}`}>
                      {selected.active ? 'active' : 'inactive'}
                    </Badge>
                    {injectedIds.has(selected.id) && (
                      <Badge variant="outline" className="text-xs text-blue-400 border-blue-500/30">injected</Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {!selected.active && (
                      <Button variant="ghost" size="icon-sm" title="Reactivate" onClick={() => reactivateHint(selected.id)}>
                        <RotateCcw className="h-3.5 w-3.5 text-green-500" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon-sm" title="Edit" onClick={() => openEditDialog(selected)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {selected.active ? (
                      <Button variant="ghost" size="icon-sm" title="Deactivate" onClick={() => void deactivateHint(selected.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    ) : (
                      <Button variant="ghost" size="icon-sm" title="Delete permanently" onClick={() => void deleteHint(selected.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Pattern */}
                {selected.pattern && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Pattern</Label>
                    <p className="text-sm mt-1 leading-relaxed">{selected.pattern}</p>
                  </div>
                )}

                {/* Antipattern */}
                {selected.antipattern && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Antipattern</Label>
                    <p className="text-sm mt-1 leading-relaxed text-red-400">{selected.antipattern}</p>
                  </div>
                )}

                {/* Selector / Element */}
                {(selected.selector || selected.element) && (
                  <div className="grid grid-cols-2 gap-4">
                    {selected.selector && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Selector</Label>
                        <code className="text-xs px-1.5 py-0.5 bg-muted rounded block mt-1 break-all">{selected.selector}</code>
                      </div>
                    )}
                    {selected.element && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Element</Label>
                        <p className="text-sm mt-1">{selected.element}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Track record, generation before healing.
                    That is the order the work actually happens in: a test is
                    generated first and only healed later, if it breaks. The
                    panel used to lead with healing, which asked the reader to
                    start in the middle of the story — and for a hint that has
                    been generated with but never healed, led with a row of
                    zeros. */}

                {/* "Adopted" here means the GENERATED test reflected the hint
                    (there's no diff to judge, unlike a heal). "—" rate means
                    the hint hasn't been injected into a generation yet (or
                    predates measurement). #1657 */}
                <div>
                  <Label className="text-xs text-muted-foreground">Generation track record</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    <EfficacyStat
                      value={selected.injectedCountGenerator || 0}
                      label="Injected"
                      tip="Times this hint was placed into the generator's prompt."
                    />
                    <EfficacyStat
                      value={selected.adoptedCountGenerator || 0}
                      label="Adopted"
                      tip="Times the generated test reflected the hint's advice. A subset of Injected."
                    />
                    <EfficacyStat
                      value={selected.effectiveCountGenerator || 0}
                      label="Effective"
                      valueClass="text-green-500"
                      tip="Times the hint was adopted and the generated test passed on its first run."
                    />
                    <EfficacyStat
                      value={Math.max(0, (selected.adoptedCountGenerator || 0) - (selected.effectiveCountGenerator || 0))}
                      label="Failed"
                      valueClass="text-amber-500"
                      tip="Times the hint was adopted but the generated test still failed on its first run."
                    />
                  </div>
                  <div className="mt-3 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1 text-muted-foreground">
                        Adoption rate
                        <Tooltip content="Share of generation injections the generator acted on — adopted ÷ injected, both counted during generation. '—' means this hint hasn't been injected into a generation yet." triggerLabel="About generation adoption rate" align="start">
                          <Info aria-hidden="true" className="h-2.5 w-2.5" />
                        </Tooltip>
                      </span>
                      <span className="tabular-nums">
                        {selected.generatorAdoptionRate == null
                          ? '—'
                          : `${Math.round(selected.generatorAdoptionRate * 100)}%`}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground">Heal track record</Label>

                  {/* Funnel: heal-injected → adopted → effective / failed.
                      Heal-scoped (#1656): adoption is judged on the heal
                      diff, so the whole funnel uses the heal counters.
                      Mixing in generation injections — which can't be
                      adopted until #1657 — is exactly what made the old
                      rate misleading. */}
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    <EfficacyStat
                      value={selected.injectedCountHealer || 0}
                      label="Injected"
                      tip="Times this hint was placed into a healer's prompt."
                    />
                    <EfficacyStat
                      value={selected.adoptedCountHealer || 0}
                      label="Adopted"
                      tip="Times the healer actually followed the hint, judged on the heal diff. A subset of Injected."
                    />
                    <EfficacyStat
                      value={selected.effectiveCountHealer || 0}
                      label="Effective"
                      valueClass="text-green-500"
                      tip="Times the hint was adopted and the heal then passed. A subset of Adopted."
                    />
                    <EfficacyStat
                      value={Math.max(0, (selected.adoptedCountHealer || 0) - (selected.effectiveCountHealer || 0))}
                      label="Failed"
                      valueClass="text-amber-500"
                      tip="Times the hint was adopted but the heal still failed. Adopted = Effective + Failed."
                    />
                  </div>

                  {/* Rates */}
                  <div className="mt-3 space-y-1.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1 text-muted-foreground">
                        Adoption rate
                        <Tooltip content="Share of heal injections the healer acted on — adopted ÷ injected, both counted during heals. '—' means this hint hasn't been injected into a heal yet." triggerLabel="About adoption rate" align="start">
                          <Info aria-hidden="true" className="h-2.5 w-2.5" />
                        </Tooltip>
                      </span>
                      <span className="tabular-nums">
                        {selected.healAdoptionRate == null
                          ? '—'
                          : `${Math.round(selected.healAdoptionRate * 100)}%`}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1 text-muted-foreground">
                        Efficacy
                        <Tooltip content="How often an adopted hint led to a passing heal — effective ÷ adopted, Beta-smoothed so a small sample isn't over-confident. The same number the Analytics report shows." triggerLabel="About efficacy" align="start">
                          <Info aria-hidden="true" className="h-2.5 w-2.5" />
                        </Tooltip>
                      </span>
                      {selected.bucket === 'unrated' ? (
                        <span className="text-muted-foreground">Unrated · needs ≥15 adoptions</span>
                      ) : (
                        <span
                          className={`tabular-nums font-medium ${
                            selected.bucket === 'effective' ? 'text-green-500' : 'text-red-500'
                          }`}
                        >
                          {Math.round(selected.efficacyRate * 100)}%{' · '}
                          {selected.bucket === 'effective' ? 'effective' : 'dead weight'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Why a note that is off was turned off (#2274).
                    Shown for every inactive note, not only the ones
                    carrying a reason: a note switched off before
                    OttoTester recorded reasons would otherwise show
                    nothing, which reads as "no explanation needed"
                    rather than "we do not know".
                    The label no longer claims the system did it —
                    a person can turn a note off from this panel, and
                    the reason now says which. Neutral styling rather
                    than the red it used to carry, because reaching
                    the note limit is routine, not a fault. */}
                {!selected.active && (
                  <div className="p-3 rounded-lg bg-muted/50 border">
                    <Label className="text-xs text-muted-foreground">Why it was turned off</Label>
                    <p className="text-xs mt-1">
                      {selected.deactivationReason
                        ?? 'Unknown. This note was turned off before OttoTester started recording reasons.'}
                    </p>
                  </div>
                )}

                {/* Lifecycle */}
                <div className="border-t pt-4">
                  <div className="grid grid-cols-2 gap-y-2 text-xs">
                    <span className="flex items-center gap-1 text-muted-foreground">
                      Confirmed
                      <Tooltip content="Times this hint was independently re-learned in a later run — how recurrent the pattern is. Separate from efficacy: a hint can be confirmed often yet rarely adopted." triggerLabel="About confirmed" align="start">
                        <Info aria-hidden="true" className="h-2.5 w-2.5" />
                      </Tooltip>
                    </span>
                    <span>{selected.confirmationCount}×</span>
                    <span className="text-muted-foreground">Created</span>
                    <span>{formatLocalDateTime(selected.createdAt, tz)}</span>
                    <span className="text-muted-foreground">Last confirmed</span>
                    <span>{formatLocalDateTime(selected.lastConfirmedAt, tz)}</span>
                  </div>
                </div>
              </div>
            )
          } />
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingHint ? 'Edit memory entry' : 'Add memory entry'}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            {!editingHint && (
              <div className="space-y-2">
                <Label>Category</Label>
                <Select options={CATEGORY_OPTIONS} value={formCategory} onChange={e => setFormCategory(e.target.value)} />
              </div>
            )}
            <div className="space-y-2">
              <Label>Element <span className="text-muted-foreground font-normal">(what UI element)</span></Label>
              <Input value={formElement} onChange={e => setFormElement(e.target.value)} placeholder="e.g. Log In button" />
            </div>
            <div className="space-y-2">
              <Label>Selector <span className="text-muted-foreground font-normal">(Playwright locator)</span></Label>
              <Input value={formSelector} onChange={e => setFormSelector(e.target.value)} placeholder="e.g. getByRole('button', { name: 'Log In' })" />
            </div>
            <div className="space-y-2">
              <Label>Pattern <span className="text-muted-foreground font-normal">(what to do)</span></Label>
              <Input value={formPattern} onChange={e => setFormPattern(e.target.value)} placeholder="e.g. Use getByRole('button', { name: 'Log In' }) for login." />
            </div>
            <div className="space-y-2">
              <Label>Antipattern <span className="text-muted-foreground font-normal">(what to avoid)</span></Label>
              <Input value={formAntipattern} onChange={e => setFormAntipattern(e.target.value)} placeholder="e.g. Don't use .btn-primary — too generic" />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={saveAction.isRunning}>Cancel</Button>
            <Button
              onClick={saveAction.trigger}
              disabled={(!formPattern && !formSelector) || saveAction.isRunning}
            >
              {saveAction.isRunning ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Type-DELETE wipe of this variant's entire hint corpus.
          Same affordance as "Delete All Tests" on the Tests page. */}
      {confirmingDeleteAll && (
        <ConfirmDeleteDialog
          title="Delete Agent Memory"
          message={`Permanently delete all ${hints.length} memory ${hints.length === 1 ? 'entry' : 'entries'} for this variant. This cannot be undone — every hint the agents have learned for this variant will be lost.`}
          onConfirm={deleteAllMemories}
          onCancel={() => setConfirmingDeleteAll(false)}
        />
      )}
    </div>
  )
}
